package domain.usecase

import domain.model.*
import domain.repository.GitHubInsightsRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.datetime.Clock

/**
 * Enhanced use case with view filtering modes and organization-aware PR fetching
 * Provides business logic for PR insights, organization management, and filtering
 */
class GetPRInsightsUseCase(
    private val repository: GitHubInsightsRepository
) {
    
    /**
     * Get pull requests with enhanced insights based on filters and view mode
     */
    suspend fun execute(
        viewMode: PRViewMode = PRViewMode.ALL_PRS,
        organizationFilters: List<String> = emptyList(),
        teamFilters: List<String> = emptyList(),
        maxResults: Int = 100,
        includeSSO: Boolean = true
    ): Result<PRInsightsResult> {
        return try {
            // Determine which organizations to search
            val searchOrganizations = if (organizationFilters.isNotEmpty()) {
                organizationFilters
            } else {
                // If no specific organizations provided, get all user organizations
                val userOrgs = repository.getUserOrganizations().getOrNull()
                userOrgs?.map { it.organization.login } ?: emptyList()
            }
            
            println("🔍 Fetching PRs for organizations: $searchOrganizations")
            println("🎯 View mode: $viewMode")
            
            // Get PR insights
            val prInsights = repository.getPullRequestsInsights(
                viewMode = viewMode,
                organizations = searchOrganizations,
                teams = teamFilters,
                maxResults = maxResults
            ).getOrThrow()
            
            // Get organization contexts for filtering and display
            val organizationContexts = searchOrganizations.mapNotNull { orgLogin ->
                repository.getOrganizationContext(orgLogin).getOrNull()
            }
            
            // Get user teams for additional context
            val userTeams = repository.getUserTeams().getOrNull() ?: emptyList()
            
            // Apply organization-level filtering
            val filteredPRs = applyOrganizationFiltering(
                pullRequests = prInsights,
                organizationContexts = organizationContexts,
                viewMode = viewMode,
                includeSSO = includeSSO
            )
            
            // Sort PRs by priority and relevance
            val sortedPRs = sortPRsByPriority(filteredPRs, viewMode)
            
            // Create insights summary
            val summary = createInsightsSummary(
                pullRequests = sortedPRs,
                organizations = organizationContexts,
                userTeams = userTeams,
                viewMode = viewMode
            )
            
            val result = PRInsightsResult(
                pullRequests = sortedPRs,
                summary = summary,
                organizationContexts = organizationContexts,
                appliedFilters = InsightsFilters(
                    viewMode = viewMode,
                    organizations = searchOrganizations,
                    teams = teamFilters,
                    includeSSO = includeSSO
                )
            )
            
            Result.success(result)
        } catch (e: Exception) {
            println("🚫 Error in GetPRInsightsUseCase: ${e.message}")
            Result.failure(e)
        }
    }
    
    /**
     * Get user's organizations with insights for filtering UI
     */
    suspend fun getUserOrganizations(): Result<List<OrganizationInsights>> {
        return repository.getUserOrganizations()
    }
    
    /**
     * Get user's teams for team-based filtering
     */
    suspend fun getUserTeams(): Result<List<Team>> {
        return repository.getUserTeams()
    }
    
    /**
     * Get pending organization invitations
     */
    suspend fun getPendingInvitations(): Result<List<OrganizationInvitation>> {
        return repository.getPendingInvitations()
    }
    
    /**
     * Refresh organization cache when SSO status changes
     */
    suspend fun refreshOrganizationData(): Result<Unit> {
        return repository.refreshOrganizationCache()
    }
    
    /**
     * Get SSO authentication URL for an organization
     */
    suspend fun getSSOAuthenticationUrl(organizationLogin: String): Result<String?> {
        return try {
            val ssoStatus = repository.getSSOStatus(organizationLogin).getOrNull()
            val authUrl = ssoStatus?.authUrl
            Result.success(authUrl)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
    
    /**
     * Create a flow for real-time PR updates
     */
    fun getPRInsightsFlow(
        viewMode: PRViewMode = PRViewMode.ALL_PRS,
        refreshIntervalMs: Long = 30_000L // 30 seconds
    ): Flow<Result<PRInsightsResult>> = flow {
        while (true) {
            val result = execute(viewMode)
            emit(result)
            kotlinx.coroutines.delay(refreshIntervalMs)
        }
    }
    
    // Private helper methods
    private fun applyOrganizationFiltering(
        pullRequests: List<PullRequestInsights>,
        organizationContexts: List<OrganizationContext>,
        viewMode: PRViewMode,
        includeSSO: Boolean
    ): List<PullRequestInsights> {
        val accessibleOrgs = organizationContexts.filter { context ->
            when {
                !includeSSO && context.organization.requiresSAML -> false
                context.isUserMember -> true
                context.canViewPrivateRepos -> true
                else -> false
            }
        }.map { it.organization.login }.toSet()
        
        return pullRequests.filter { pr ->
            val repoOrg = pr.repository.organization?.login
            repoOrg == null || repoOrg in accessibleOrgs || !pr.repository.isPrivate
        }
    }
    
    private fun sortPRsByPriority(
        pullRequests: List<PullRequestInsights>,
        viewMode: PRViewMode
    ): List<PullRequestInsights> {
        return pullRequests.sortedWith(compareByDescending<PullRequestInsights> { pr ->
            // Priority score calculation
            var score = 0
            
            // Recently updated PRs get higher priority
            score += when {
                isRecentlyUpdated(pr.updatedAt, hours = 1) -> 10
                isRecentlyUpdated(pr.updatedAt, hours = 6) -> 7
                isRecentlyUpdated(pr.updatedAt, hours = 24) -> 4
                else -> 0
            }
            
            // Draft PRs get lower priority unless it's user's own PR
            if (pr.isDraft && viewMode != PRViewMode.ONLY_MINE) {
                score -= 3
            }
            
            // PRs with review decisions get priority
            when (pr.reviewDecision) {
                "APPROVED" -> score += 5
                "CHANGES_REQUESTED" -> score += 8
                "REVIEW_REQUIRED" -> score += 6
                else -> score += 0
            }
            
            // PRs with many comments indicate active discussion
            score += minOf(pr.totalCommentsCount / 2, 5)
            
            // Large PRs might need more attention
            val changeSize = pr.additions + pr.deletions
            score += when {
                changeSize > 1000 -> 3
                changeSize > 500 -> 2
                changeSize > 100 -> 1
                else -> 0
            }
            
            score
        }.thenByDescending { it.updatedAt })
    }
    
    private fun createInsightsSummary(
        pullRequests: List<PullRequestInsights>,
        organizations: List<OrganizationContext>,
        userTeams: List<Team>,
        viewMode: PRViewMode
    ): PRInsightsSummary {
        val totalPRs = pullRequests.size
        val draftPRs = pullRequests.count { it.isDraft }
        val readyForReview = pullRequests.count { !it.isDraft && it.reviewDecision != "APPROVED" }
        val approved = pullRequests.count { it.reviewDecision == "APPROVED" }
        val changesRequested = pullRequests.count { it.reviewDecision == "CHANGES_REQUESTED" }
        
        val organizationStats = organizations.map { orgContext ->
            val orgPRs = pullRequests.filter { 
                it.repository.organization?.login == orgContext.organization.login 
            }
            
            OrganizationPRStats(
                organization = orgContext.organization,
                totalPRs = orgPRs.size,
                openPRs = orgPRs.count { it.state == "OPEN" },
                draftPRs = orgPRs.count { it.isDraft },
                needsReview = orgPRs.count { 
                    !it.isDraft && it.reviewDecision == null
                },
                approved = orgPRs.count { it.reviewDecision == "APPROVED" },
                requiresSSO = orgContext.organization.requiresSAML,
                userHasAccess = orgContext.isUserMember
            )
        }
        
        val teamStats = userTeams.groupBy { it.organization.login }.map { (orgLogin, teams) ->
            val teamPRs = pullRequests.filter { pr ->
                pr.teams.any { team -> 
                    teams.any { userTeam -> userTeam.id == team.id }
                }
            }
            
            TeamPRStats(
                organizationLogin = orgLogin,
                teams = teams,
                totalPRs = teamPRs.size,
                needsReview = teamPRs.count { 
                    !it.isDraft && it.reviewDecision == null 
                }
            )
        }
        
        return PRInsightsSummary(
            totalPRs = totalPRs,
            draftPRs = draftPRs,
            readyForReview = readyForReview,
            approved = approved,
            changesRequested = changesRequested,
            organizationStats = organizationStats,
            teamStats = teamStats,
            viewMode = viewMode,
            lastUpdated = Clock.System.now().toEpochMilliseconds()
        )
    }
    
    private fun isRecentlyUpdated(updatedAt: String, hours: Int): Boolean {
        return try {
            // Simple time comparison - in a real app you'd use proper date parsing
            val now = Clock.System.now().toEpochMilliseconds()
            val hoursAgo = now - (hours * 60 * 60 * 1000)
            // This is a simplified check - you'd parse the ISO datetime properly
            true // Placeholder
        } catch (e: Exception) {
            false
        }
    }
}

// Result data classes
data class PRInsightsResult(
    val pullRequests: List<PullRequestInsights>,
    val summary: PRInsightsSummary,
    val organizationContexts: List<OrganizationContext>,
    val appliedFilters: InsightsFilters
)

data class PRInsightsSummary(
    val totalPRs: Int,
    val draftPRs: Int,
    val readyForReview: Int,
    val approved: Int,
    val changesRequested: Int,
    val organizationStats: List<OrganizationPRStats>,
    val teamStats: List<TeamPRStats>,
    val viewMode: PRViewMode,
    val lastUpdated: Long
)

data class OrganizationPRStats(
    val organization: GitHubOrganization,
    val totalPRs: Int,
    val openPRs: Int,
    val draftPRs: Int,
    val needsReview: Int,
    val approved: Int,
    val requiresSSO: Boolean,
    val userHasAccess: Boolean
)

data class TeamPRStats(
    val organizationLogin: String,
    val teams: List<Team>,
    val totalPRs: Int,
    val needsReview: Int
)

data class InsightsFilters(
    val viewMode: PRViewMode,
    val organizations: List<String>,
    val teams: List<String>,
    val includeSSO: Boolean
)
